import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["isActive", "isCurrentPage", "aria-label", "aria-labelledby", "className", "children", "renderMenuContent", "menuLinkName", "focusRef", "onBlur", "onClick", "onKeyDown"];
/**
 * Copyright IBM Corp. 2016, 2023
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { ChevronDown } from '@carbon/icons-react';
import cx from 'classnames';
import React, { forwardRef, useContext, useState, useRef, Children, isValidElement, cloneElement } from 'react';
import { Escape, Enter, Space } from '../../internal/keyboard/keys.js';
import { matches } from '../../internal/keyboard/match.js';
import { AriaLabelPropType } from '../../prop-types/AriaPropTypes.js';
import { PrefixContext } from '../../internal/usePrefix.js';
import { deprecate } from '../../prop-types/deprecate.js';
import { composeEventHandlers } from '../../tools/events.js';
import { useMergedRefs } from '../../internal/useMergedRefs.js';
var frFn = forwardRef;
var HeaderMenu = frFn(function (props, ref) {
  var _cx, _cx2;
  var isActive = props.isActive,
    isCurrentPage = props.isCurrentPage,
    ariaLabel = props['aria-label'],
    ariaLabelledBy = props['aria-labelledby'],
    customClassName = props.className,
    children = props.children,
    MenuContent = props.renderMenuContent,
    menuLinkName = props.menuLinkName,
    focusRef = props.focusRef,
    onBlur = props.onBlur,
    onClick = props.onClick,
    onKeyDown = props.onKeyDown,
    rest = _objectWithoutProperties(props, _excluded);
  var prefix = useContext(PrefixContext);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    expanded = _useState2[0],
    setExpanded = _useState2[1];
  var menuButtonRef = useRef(null);
  var subMenusRef = useRef(null);
  var itemRefs = useRef([]);
  var mergedButtonRef = useMergedRefs([ref, focusRef, menuButtonRef]);

  /**
   * Toggle the expanded state of the menu on click.
   */
  var handleOnClick = function handleOnClick(e) {
    if (!subMenusRef.current || e.target instanceof Node && !subMenusRef.current.contains(e.target)) {
      e.preventDefault();
    }
    setExpanded(function (prev) {
      return !prev;
    });
  };

  /**
   * Keyboard event handler for the entire menu.
   */
  var handleOnKeyDown = function handleOnKeyDown(event) {
    // Handle enter or space key for toggling the expanded state of the menu.
    if (matches(event, [Enter, Space])) {
      event.stopPropagation();
      event.preventDefault();
      setExpanded(function (prev) {
        return !prev;
      });
      return;
    }
  };

  /**
   * Handle our blur event from our underlying menuitems. Will mostly be used
   * for closing our menu in response to a user clicking off or tabbing out of
   * the menu or menubar.
   */
  var handleOnBlur = function handleOnBlur(event) {
    var _subMenusRef$current;
    // Close the menu on blur when the related target is not a sibling menu item
    // or a child in a submenu
    var siblingItemBlurredTo = itemRefs.current.find(function (element) {
      return element === event.relatedTarget;
    });
    var childItemBlurredTo = (_subMenusRef$current = subMenusRef.current) === null || _subMenusRef$current === void 0 ? void 0 : _subMenusRef$current.contains(event.relatedTarget);
    if (!siblingItemBlurredTo && !childItemBlurredTo) {
      setExpanded(false);
    }
  };

  /**
   * Handles individual menuitem refs. We assign them to a class instance
   * property so that we can properly manage focus of our children.
   */
  var handleItemRef = function handleItemRef(index) {
    return function (node) {
      itemRefs.current[index] = node;
    };
  };
  var handleMenuClose = function handleMenuClose(event) {
    // Handle ESC keydown for closing the expanded menu.
    if (matches(event, [Escape]) && expanded) {
      event.stopPropagation();
      event.preventDefault();
      setExpanded(false);

      // Return focus to menu button when the user hits ESC.
      if (menuButtonRef.current) {
        menuButtonRef.current.focus();
      }
    }
  };
  var hasActiveDescendant = function hasActiveDescendant(childrenArg) {
    return Children.toArray(childrenArg).some(function (child) {
      if (! /*#__PURE__*/isValidElement(child)) {
        return false;
      }
      var _child$props = child.props,
        isActive = _child$props.isActive,
        isCurrentPage = _child$props.isCurrentPage,
        children = _child$props.children;
      return isActive || isCurrentPage || Array.isArray(children) && hasActiveDescendant(children);
    });
  };

  /**
   * We capture the `ref` for each child inside of `this.items` to properly
   * manage focus. In addition to this focus management, all items receive a
   * `tabIndex: -1` so the user won't hit a large number of items in their tab
   * sequence when they might not want to go through all the items.
   */
  var renderMenuItem = function renderMenuItem(item, index) {
    if (/*#__PURE__*/isValidElement(item)) {
      return /*#__PURE__*/cloneElement(item, {
        ref: handleItemRef(index)
      });
    }
    return item;
  };
  var accessibilityLabel = {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy
  };
  var itemClassName = cx((_cx = {}, _defineProperty(_cx, "".concat(prefix, "--header__submenu"), true), _defineProperty(_cx, "".concat(customClassName), !!customClassName), _cx));
  var isActivePage = isActive ? isActive : isCurrentPage;
  var linkClassName = cx((_cx2 = {}, _defineProperty(_cx2, "".concat(prefix, "--header__menu-item"), true), _defineProperty(_cx2, "".concat(prefix, "--header__menu-title"), true), _defineProperty(_cx2, "".concat(prefix, "--header__menu-item--current"), isActivePage || hasActiveDescendant(children) && !expanded), _cx2));

  // Notes on eslint comments and based on the examples in:
  // https://www.w3.org/TR/wai-aria-practices/examples/menubar/menubar-1/menubar-1.html#
  // - The focus is handled by the <a> menuitem, onMouseOver is for mouse
  // users
  // - aria-haspopup can definitely have the value "menu"
  // - aria-expanded is on their example node with role="menuitem"
  // - href can be set to javascript:void(0), ideally this will be a button
  return /*#__PURE__*/React.createElement("li", _extends({}, rest, {
    className: itemClassName,
    onKeyDown: composeEventHandlers([onKeyDown, handleMenuClose]),
    onClick: composeEventHandlers([onClick, handleOnClick]),
    onBlur: composeEventHandlers([onBlur, handleOnBlur]),
    ref: ref
  }), /*#__PURE__*/React.createElement("a", _extends({
    // eslint-disable-line jsx-a11y/role-supports-aria-props,jsx-a11y/anchor-is-valid
    "aria-haspopup": "menu" // eslint-disable-line jsx-a11y/aria-proptypes
    ,

    "aria-expanded": expanded,
    className: linkClassName,
    href: "#",
    onKeyDown: handleOnKeyDown,
    ref: mergedButtonRef,
    tabIndex: 0
  }, accessibilityLabel), menuLinkName, MenuContent ? /*#__PURE__*/React.createElement(MenuContent, null) : /*#__PURE__*/React.createElement(ChevronDown, {
    className: "".concat(prefix, "--header__menu-arrow")
  })), /*#__PURE__*/React.createElement("ul", _extends({}, accessibilityLabel, {
    ref: subMenusRef,
    className: "".concat(prefix, "--header__menu")
  }), Children.map(children, renderMenuItem)));
});
HeaderMenu.displayName = 'HeaderMenu';
export { HeaderMenu, HeaderMenu as default };